import React from 'react';
import { IndicatorSectionKey } from '../../ImpactIndicator/utils';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBookHeart,
  faHandHoldingHeart,
  faClapperboard,
  faPersonRunning,
  faBarsProgress,
} from '@fortawesome/pro-light-svg-icons';

export interface HeadingProps {
  nextSectionName: string;
  currentSectionKey: IndicatorSectionKey;
  currentSectionName: string;
  nextSectionTotalIndicators: number;
  howManyIndicatorsLeftInCurrentSection: number;
}

export const nextSectionIcon = ({
  currentSectionKey,
}: HeadingProps): IconDefinition | IconProp => {
  switch (currentSectionKey) {
    case 'capacityDevelopment':
      return faPersonRunning;
    case 'reach':
      return faHandHoldingHeart;
    case 'learn':
      return faBookHeart;
    case 'act':
      return faClapperboard;
    default:
      return faBarsProgress;
  }
};

export const nextSectionHeading = ({
  currentSectionKey,
}: HeadingProps): React.ReactNode => {
  switch (currentSectionKey) {
    case 'capacityDevelopment':
      return <>You’re off to the races!</>;
    case 'reach':
      return <>Look at you go!</>;
    case 'learn':
      return <>We’re learning so much about your work!</>;
    case 'act':
      return <>Lights, camera, action!</>;
    default:
      return (
        <>
          Great work!
          <br />
          Your Impact Model is really coming together.
        </>
      );
  }
};

const getIndicatorText = (total: number): string =>
  total === 1 ? 'indicator' : 'indicators';

export const nextSectionSubHeading = ({
  nextSectionName,
  nextSectionTotalIndicators,
  currentSectionKey,
  currentSectionName,
}: HeadingProps): React.ReactNode => {
  switch (currentSectionKey) {
    case 'capacityDevelopment':
      return (
        <>
          Great job forecasting all the indicators for your{' '}
          <strong>{currentSectionName}</strong> stage!
          <br />
          <br />
          Next up is the <strong>{nextSectionName}</strong> stage, which has{' '}
          <strong>
            {nextSectionTotalIndicators}{' '}
            {getIndicatorText(nextSectionTotalIndicators)}
          </strong>{' '}
          to forecast.
        </>
      );
    case 'reach':
      return (
        <>
          Now that you’ve forecasted your <strong>{currentSectionName}</strong>{' '}
          stage, let’s see how quickly you can ... reach the end of the next
          stage. See what we did there?
          <br />
          <br />
          Speaking of the next stage, next up is{' '}
          <strong>{nextSectionName}</strong>, which has{' '}
          <strong>
            {nextSectionTotalIndicators}{' '}
            {getIndicatorText(nextSectionTotalIndicators)}
          </strong>{' '}
          to forecast.
        </>
      );
    case 'learn':
      return (
        <>
          Just like the beneficiaries you just forecasted for your{' '}
          <strong>{currentSectionName}</strong> stage.
          <br />
          <br />
          Next up: the <strong>{nextSectionName}</strong> stage, which has{' '}
          <strong>
            {nextSectionTotalIndicators}{' '}
            {getIndicatorText(nextSectionTotalIndicators)}
          </strong>{' '}
          to forecast.
        </>
      );
    case 'act':
      return (
        <>
          Now we know all about how your beneficiaries{' '}
          <strong>{currentSectionName}</strong> on what they learned, and you
          know what? We think that’s pretty awesome.
          <br />
          <br />
          Next up: The final stage. The big one. The main attraction. The one
          you put in all the work for. We present to you: the{' '}
          <strong>{nextSectionName}</strong> stage, which has{' '}
          <strong>
            {nextSectionTotalIndicators}{' '}
            {getIndicatorText(nextSectionTotalIndicators)}
          </strong>{' '}
          to forecast.
        </>
      );

    default:
      return (
        <>
          Your forecast is off to a great start.
          <br />
          <br />
          The next stage up is <strong>{nextSectionName}</strong>, which has{' '}
          <strong>
            {nextSectionTotalIndicators}{' '}
            {getIndicatorText(nextSectionTotalIndicators)}
          </strong>{' '}
          to forecast.
        </>
      );
  }
};

export const nextIndicatorHeading = (props: HeadingProps): React.ReactNode => {
  return (
    <>
      Great work!
      <br />
      Your Impact Model is really coming together.
    </>
  );
};

export const nextIndicatorSubHeading = ({
  currentSectionName,
  howManyIndicatorsLeftInCurrentSection,
}: HeadingProps): React.ReactNode => (
  <>
    Your forecast is off to a great start.
    <br />
    Only{' '}
    <strong>
      {howManyIndicatorsLeftInCurrentSection === 1
        ? '1 indicator'
        : `${howManyIndicatorsLeftInCurrentSection} indicators`}
    </strong>{' '}
    left to forecast in <strong>{currentSectionName}</strong> section.
  </>
);

export const allCompletedIndicatorsHeading = (
  props: HeadingProps,
): React.ReactNode => <>Wow, look at all that impact!</>;

export const allCompletedIndicatorsSubHeading = (
  props: HeadingProps,
): React.ReactNode => (
  <>
    You did it! You forecasted all of your <strong>Succeed</strong> indicators.
    This is a huge step in the reporting process and we truly want to thank you
    for taking the time to answer all of those questions. It sets you up for —
    you guessed it — success throughout the reporting process.
    <br />
    <br />
    Whew! You’re almost done. We just have a few more sections of your report to
    get through.
  </>
);
